/**
 * SEOHead — the single, canonical way to inject <title>, meta description,
 * canonical URL, OpenGraph, Twitter, and robots directives on any page.
 *
 * Why a component and not a helper?
 *  - Every page owns its own SEO story.  Bugs like "Useful Links - Sri Durga
 *    | Checkout" from iter79 crept in exactly because some pages had NO
 *    Helmet at all — Google's crawler then auto-generated a title from
 *    footer links.  Passing SEOHead in every page forces authors to think
 *    about a unique title.
 *  - Utility pages (Checkout, Cart, Login, Account) MUST NOT be indexed.
 *    A single `noindex` prop drops the correct robots meta so we can't
 *    forget it.
 *
 * Usage
 *   <SEOHead title="Product name | Sri Durga Foods"
 *            description="…up to 160 chars…"
 *            canonical="/product-path"          // path OR absolute
 *            image="/icon-512.png"              // OG/Twitter image (path OR abs)
 *            noindex                              // dropdown / auth / cart etc.
 *   >
 *     <script type="application/ld+json">{jsonLdString}</script>
 *   </SEOHead>
 */
import { Helmet } from "react-helmet-async";
import { SITE_ORIGIN, truncate } from "../lib/seo";

const DEFAULT_IMAGE = `${SITE_ORIGIN}/icon-512.png`;
const DEFAULT_DESCRIPTION =
  "Handcrafted Andhra pickles, culinary pastes, masalas, podis, wood-pressed oils and flours from Sri Durga Foods — 25+ years of Nellore tradition, delivered pan-India.";

function absolutise(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_ORIGIN}${url.startsWith("/") ? url : `/${url}`}`;
}

export default function SEOHead({
  title,
  description,
  canonical,
  image,
  noindex = false,
  ogType = "website",
  children,
}) {
  const t = (title || "Sri Durga Foods | Authentic Andhra Pickles, Spices & Wood-Pressed Oils").trim();
  const d = truncate(description || DEFAULT_DESCRIPTION, 160);
  const canonicalHref = canonical ? absolutise(canonical) : "";
  const img = absolutise(image || DEFAULT_IMAGE);

  return (
    <Helmet prioritizeSeoTags>
      <title>{t}</title>
      <meta name="description" content={d} />
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* OpenGraph */}
      <meta property="og:site_name" content="Sri Durga Foods" />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      {canonicalHref && <meta property="og:url" content={canonicalHref} />}
      <meta property="og:image" content={img} />

      {/* Twitter — inherits from OG in modern crawlers but be explicit */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />

      {children}
    </Helmet>
  );
}
